import { Operation } from "@apollo/client"
import { ErrorHandler } from "@apollo/client/link/error"
import { HStack, IconButton, useToast } from "@chakra-ui/react"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { print } from "graphql"
import LZString from "lz-string"
import { getEnv, getOperatingEnv, getTargetEnv } from "../env/env"

// A simpler representation of an operation, uses less storage.
export type SimpleApolloOperation = Pick<
  Operation,
  "operationName" | "variables"
> & {
  query: string
  hasError?: boolean
}

export function extractSimpleApolloOperation(
  operation: Operation
): SimpleApolloOperation {
  return {
    operationName: operation.operationName,
    variables: operation.variables,
    query: print(operation.query),
    hasError: false
  }
}

export function operationMatchesSimpleApolloOperation(
  operation: Operation,
  simpleOperation: SimpleApolloOperation
): boolean {
  const providedSimpleOperation = extractSimpleApolloOperation(operation)

  return (
    providedSimpleOperation.operationName === simpleOperation.operationName &&
    providedSimpleOperation.query === simpleOperation.query &&
    providedSimpleOperation.variables === simpleOperation.variables
  )
}

// Gets a link to the Apollo Sandbox for the given operation and target environment.
export function getApolloSandboxLink(
  operation: SimpleApolloOperation,
  targetLocalHost: boolean
) {
  const options: {
    document?: string
    variables?: string
    operationName?: string
    headers?: string
  } = {
    document: operation.query,
    variables: JSON.stringify(operation.variables),
    operationName: operation.operationName
  }

  // Grab the school (space) ID from localStorage (should always be available).
  const spaceId = localStorage.getItem("PW_SPACE")

  // Include the headers option if an auth_token is found in the current search params.
  if (localStorage.getItem("auth_token")) {
    options.headers = JSON.stringify({
      Authorization: "Token " + localStorage.getItem("auth_token")
    })
  }

  // Apollo uses LZString compression for their explorerURLState param.
  // See GitHub discussion: https://github.com/apollographql/apollo-studio-community/discussions/127?utm_source=chatgpt.com#discussioncomment-2150944
  const explorerURLState = LZString.compressToEncodedURIComponent(
    JSON.stringify(options)
  )

  // Query params to pass to the sandbox.
  const queryParams = `school_id=${spaceId}&explorerURLState=${explorerURLState}`

  if (targetLocalHost) {
    return `http://localhost:8000/graphql?${queryParams}`
  } else {
    const endpoint = getTargetEnv(getOperatingEnv(), {
      production: "https://gql.pathwright.com",
      staging: "https://gql.pathwrightstaging.com",
      testing: "https://gql.pantswright.com",
      development: "https://gql.pathwrightdev.com"
    })

    return `https://studio.apollographql.com/sandbox/explorer?endpoint=${endpoint}/graphql?${queryParams}`
  }
}

// A hook for toasting local devs of Apollo operation GraphQL errors.
export function useApolloOperationErrorToast() {
  const toast = useToast()
  const errorHandler: ErrorHandler = ({ operation, graphQLErrors }) => {
    // Only record error if PW Admin Util is enabled. And only toast if in non-prod envs.
    if (graphQLErrors && !getEnv("production")) {
      const operationName = operation.operationName || "Unknown Operation"

      const simpleOperation = extractSimpleApolloOperation(operation)
      const developmentLink = getApolloSandboxLink(simpleOperation, true)
      const operatingEnvLink = getApolloSandboxLink(simpleOperation, false)

      toast({
        title: "GraphQL Error",
        description: (
          <HStack w="100%">
            <Link
              color="white"
              colorScheme="whiteAlpha"
              href={developmentLink}
              underlineBehavior="always"
            >
              {operationName}
            </Link>
            <IconButton
              as={Link}
              href={operatingEnvLink}
              color="white"
              colorScheme="whiteAlpha"
              variant="ghost"
              minH="2em"
              minW="2em"
              ml="auto"
              isRound={true}
              aria-label="Link"
              onClick={(e) => e.stopPropagation()}
              icon={<Pathicon icon="globe-fill" />}
            />
          </HStack>
        ),
        status: "error",
        duration: 3000,
        isClosable: true,
        icon: <Pathicon icon="computer-frown" />,
        containerStyle: {
          // Force light mode Close button.
          // @ts-expect-error: this works, but fails type checking
          "& button": {
            color: "white !important"
          }
        }
      })
    }
  }

  return errorHandler
}
